import React, {
	ElementType,
	cloneElement,
	createContext,
	forwardRef,
	isValidElement,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
	HTMLProps,
	ReactElement,
	ReactNode,
} from "react";
import {
	autoUpdate,
	flip,
	FloatingPortal,
	offset,
	shift,
	useDelayGroup,
	useDelayGroupContext,
	useDismiss,
	useFloating,
	useFocus,
	useHover,
	useId,
	useInteractions,
	useMergeRefs,
	useRole,
	useTransitionStyles,
	useClientPoint,
	safePolygon,
} from "@floating-ui/react";
import {arrow, autoPlacement} from "@floating-ui/react-dom";
import type {TTooltipOptions} from "./types";
import StandardTooltip from "components/TooltipComponents/StandardTooltip";
import {ITooltipComponent} from "components/TooltipComponents/types";

export function useTooltip({
	allowInteraction = false,
	crossAxis = 0,
	followMouse = false,
	initialOpen = false,
	mainAxis = 5,
	onOpenChange: setControlledOpen,
	open: controlledOpen,
	placement,
	showArrow = false,
}: TTooltipOptions & {mainAxis?: number; crossAxis?: number} = {}) {
	const [uncontrolledOpen, setUncontrolledOpen] = useState(initialOpen);
	const arrowRef = useRef<HTMLDivElement | null>(null);
	const [arrowPosition, setArrowPosition] = useState({x: 0, y: 0, placement: "top"});
	const open = controlledOpen ?? uncontrolledOpen;
	const setOpen = setControlledOpen ?? setUncontrolledOpen;
	const {delay} = useDelayGroupContext();

	// Memoize the middleware array to avoid unnecessary recalculations.
	const middlewares = useMemo(() => {
		const m = [offset({mainAxis, crossAxis}), flip(), shift()];
		if (!placement) {
			m.push(autoPlacement());
		}
		// Only add the arrow middleware if showArrow is true and the arrow element exists.
		if (showArrow && arrowRef.current) {
			m.push(arrow({element: arrowRef.current}));
		}

		return m;
	}, [mainAxis, crossAxis, placement, showArrow]);

	const data = useFloating({
		placement,
		open,
		onOpenChange: setOpen,
		whileElementsMounted: autoUpdate,
		middleware: middlewares,
	});

	// Effect to update the arrow position when middleware returns new position values.
	useEffect(() => {
		const arrowData = data.middlewareData.arrow;
		if (arrowData) {
			setArrowPosition({
				x: arrowData.x ?? 0,
				y: arrowData.y ?? 0,
				placement: data.placement,
			});
		}
	}, [data.middlewareData.arrow, data.placement]);

	const context = data.context;

	const hover = useHover(context, {
		move: true,
		enabled: controlledOpen == null,
		delay,
		handleClose: allowInteraction ? safePolygon({blockPointerEvents: true}) : undefined,
	});
	const focus = useFocus(context, {enabled: controlledOpen == null});
	const dismiss = useDismiss(context);
	const role = useRole(context, {role: "tooltip"});

	if (followMouse) {
		useClientPoint(context);
	}

	const interactions = useInteractions([hover, focus, dismiss, role]);

	// Memoize the returned tooltip state to prevent unnecessary re-renders and potential memory leaks.
	return useMemo(
		() => ({
			open,
			setOpen,
			arrowRef,
			showArrow,
			arrowPosition,
			allowInteraction,
			...interactions,
			...data,
		}),
		[open, setOpen, showArrow, arrowPosition, allowInteraction, interactions, data],
	);
}

type TooltipContextType = ReturnType<typeof useTooltip> | null;
const TooltipContext = createContext<TooltipContextType>(null);

export const useTooltipState = () => {
	const context = useContext(TooltipContext);
	if (context == null) {
		throw new Error("Tooltip components must be wrapped in <Tooltip />");
	}

	return context;
};

export function Tooltip({
	children,
	...options
}: {
	children: ReactNode;
	mainAxis?: number;
	crossAxis?: number;
} & TTooltipOptions) {
	// Use the tooltip hook to provide state and interactions.
	const tooltip = useTooltip(options);

	return <TooltipContext.Provider value={tooltip}>{children}</TooltipContext.Provider>;
}

export const TooltipTrigger = forwardRef<HTMLElement, HTMLProps<HTMLElement> & {asChild?: boolean}>(
	function TooltipTrigger({children, asChild = false, ...props}, propRef) {
		const state = useTooltipState();
		const childrenRef = (children as ReactElement).props.ref;
		const ref = useMergeRefs([state.refs.setReference, propRef, childrenRef]);

		if (asChild && isValidElement(children)) {
			return cloneElement(
				children,
				state.getReferenceProps({
					ref,
					...props,
					...children.props,
					"data-state": state.open ? "open" : "closed",
				}),
			);
		}

		return (
			<button
				ref={ref}
				data-state={state.open ? "open" : "closed"}
				{...state.getReferenceProps(props)}
			>
				{children}
			</button>
		);
	},
);

export const TooltipContent = forwardRef<
	HTMLDivElement,
	{TooltipComponent?: ElementType<ITooltipComponent>} & HTMLProps<HTMLDivElement>
>(function TooltipContent({TooltipComponent = StandardTooltip, ...rest}, propRef) {
	const state = useTooltipState();
	const id = useId();
	const {isInstantPhase, currentId} = useDelayGroupContext();
	const ref = useMergeRefs([state.refs.setFloating, propRef]);

	// Register this component with the delay group to coordinate transition timings.
	useDelayGroup(state.context, {id});

	const instantDuration = 0;
	const duration = 250;

	const {isMounted, styles} = useTransitionStyles(state.context, {
		duration: isInstantPhase
			? {
					open: instantDuration,
					close: currentId === id ? duration : instantDuration,
			  }
			: duration,
		initial: {
			opacity: 0,
		},
	});

	return (
		<FloatingPortal>
			{isMounted && (
				<div
					ref={ref}
					style={{
						left: state.x ?? 0,
						top: state.y ?? 0,
						position: state.strategy,
						pointerEvents: state.allowInteraction ? "auto" : "none",
						visibility: state.x == null ? "hidden" : "visible",
						zIndex: 110,
						...rest.style,
						...styles,
					}}
					{...state.getFloatingProps(rest)}
				>
					<TooltipComponent
						arrowPosition={state.arrowPosition}
						ref={state.arrowRef}
						showArrow={state.showArrow}
					>
						{rest.children}
					</TooltipComponent>
				</div>
			)}
		</FloatingPortal>
	);
});

Tooltip.Content = TooltipContent;
Tooltip.Trigger = TooltipTrigger;

export default Tooltip;
